import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SlidersHorizontal, ChevronDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import ColumnVisibilityMenu from "./ColumnVisibilityMenu";
import TablePagination from "./TablePagination";
import {
  getRowId,
  toggleSort,
  DEFAULT_TABLE_PAGE_SIZE,
  computeTableMinWidth,
  resolveColumnWidth,
} from "./tableUtils";
import { TableHeaderLabel } from "./TableCell";
import "./table.css";

/** Default + icon for toolbar Add / Create / New actions (Figma 670-3736). */
function isAddOrCreateAction(label) {
  if (!label || typeof label !== "string") return false;
  const normalized = label.trim().toLowerCase();
  return /^(add|create|new)/.test(normalized) || /\b(add|create|new)\b/.test(normalized);
}

function defaultVisibleColumnIds(columns) {
  return columns.filter((c) => c.visible !== false).map((c) => c.id);
}

/**
 * Hydrate visible columns from localStorage.
 * New columns (not yet in stored `known`) are shown by default so schema
 * additions appear without requiring users to reset column prefs.
 * Legacy format was a plain string[] of visible ids.
 */
function loadVisibleColumnIds(columns, storageKey) {
  const defaults = defaultVisibleColumnIds(columns);
  if (!storageKey) return new Set(defaults);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set(defaults);
    const parsed = JSON.parse(raw);

    let savedVisible;
    let knownIds;
    if (Array.isArray(parsed)) {
      savedVisible = new Set(parsed);
      knownIds = new Set(parsed);
    } else if (parsed && Array.isArray(parsed.visible)) {
      savedVisible = new Set(parsed.visible);
      knownIds = new Set(
        Array.isArray(parsed.known) && parsed.known.length
          ? parsed.known
          : parsed.visible
      );
    } else {
      return new Set(defaults);
    }

    const next = new Set();
    columns.forEach((c) => {
      if (c.visible === false) return;
      if (!knownIds.has(c.id)) {
        next.add(c.id);
      } else if (savedVisible.has(c.id)) {
        next.add(c.id);
      }
    });
    return next.size > 0 ? next : new Set(defaults);
  } catch {
    return new Set(defaults);
  }
}

function persistVisibleColumnIds(storageKey, visibleIds, columns) {
  if (!storageKey) return;
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        visible: [...visibleIds],
        known: columns.map((c) => c.id),
      })
    );
  } catch {
    /* ignore */
  }
}

function resolvePrimaryActionIcon(primaryAction) {
  if (primaryAction.icon) return primaryAction.icon;
  if (isAddOrCreateAction(primaryAction.label)) {
    return <Plus size={14} strokeWidth={2.5} aria-hidden="true" />;
  }
  return undefined;
}

/** Figma unfold_more — 14×14 filled carets (node 76:13407). */
function TableSortIcon({ active, direction }) {
  const isAsc = active && direction === "asc";
  const isDesc = active && direction === "desc";

  return (
    <span className="oh-table-sort-icon" aria-hidden="true">
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          className={[
            "oh-table-sort-icon__chev",
            "oh-table-sort-icon__chev--up",
            isAsc ? "is-active" : "",
            isDesc ? "is-muted" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          fill="currentColor"
          d="M6.99992 3.16482L5.36877 4.79597C5.26737 4.89514 5.14647 4.94472 5.00608 4.94472C4.86579 4.94472 4.74606 4.89465 4.6469 4.79451C4.54783 4.69437 4.49829 4.57372 4.49829 4.43255C4.49829 4.29139 4.54783 4.17073 4.6469 4.0706L6.64831 2.06568C6.70198 2.01473 6.75842 1.97667 6.81762 1.95149C6.87693 1.92621 6.93842 1.91357 7.0021 1.91357C7.06588 1.91357 7.12674 1.92621 7.18469 1.95149C7.24263 1.97667 7.29824 2.01473 7.35152 2.06568L9.35294 4.0706C9.45201 4.172 9.50154 4.29348 9.50154 4.43503C9.50154 4.57649 9.45147 4.69739 9.35133 4.79772C9.25129 4.89572 9.13039 4.94414 8.98865 4.94297C8.84699 4.9418 8.72663 4.89164 8.62756 4.79247L6.99992 3.16482Z"
        />
        <path
          className={[
            "oh-table-sort-icon__chev",
            "oh-table-sort-icon__chev--down",
            isDesc ? "is-active" : "",
            isAsc ? "is-muted" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          fill="currentColor"
          d="M6.99992 10.8864L8.63106 9.25526C8.73246 9.15619 8.85336 9.10666 8.99375 9.10666C9.13404 9.10666 9.25377 9.15673 9.35294 9.25687C9.45201 9.35691 9.50154 9.47751 9.50154 9.61868C9.50154 9.75985 9.45201 9.8805 9.35294 9.98064L7.35152 11.9821C7.29785 12.0351 7.24137 12.0738 7.18206 12.098C7.12285 12.1222 7.06136 12.1343 6.99758 12.1343C6.9338 12.1343 6.87304 12.1222 6.81529 12.098C6.75744 12.0738 6.70178 12.0351 6.64831 11.9821L4.6469 9.98064C4.54783 9.87923 4.49829 9.75776 4.49829 9.6162C4.49829 9.47474 4.54836 9.35385 4.6485 9.25351C4.74854 9.15561 4.86915 9.10666 5.01031 9.10666C5.15148 9.10666 5.27213 9.15619 5.37227 9.25526L6.99992 10.8864Z"
        />
      </svg>
    </span>
  );
}

function SortableHeaderButton({
  headerLabel,
  headerAlign,
  colId,
  sort,
  onSort,
  sortBtnAlignClass,
}) {
  return (
    <button
      type="button"
      className={["oh-table-sort-btn", sortBtnAlignClass(headerAlign)]
        .filter(Boolean)
        .join(" ")}
      onClick={onSort}
    >
      <span className="oh-table-sort-btn__inner">
        <TableHeaderLabel className="oh-table-sort-btn__label">
          {headerLabel}
        </TableHeaderLabel>
        <TableSortIcon
          active={sort?.column === colId}
          direction={sort?.column === colId ? sort.direction : null}
        />
      </span>
    </button>
  );
}

function isControlledPagination(pagination) {
  return Boolean(pagination && typeof pagination.onPageChange === "function");
}

function DataTable({
  title,
  columns = [],
  data = [],
  rowKey = "_id",
  selectable = false,
  selectionMode = "checkbox",
  selectedIds: controlledSelectedIds,
  onSelectionChange,
  primaryAction,
  bulkActions = [],
  onRowClick,
  sort,
  pagination,
  loading = false,
  emptyState,
  disableToolbarWhenSelected = true,
  bulkBarPlacement = "start",
  tableId,
  maxBodyHeight = 560,
  className = "",
  isRtl = false,
  showColumnSettings = true,
  settingsIcon,
  columnSettingsAriaLabel,
  toolbarExtra,
  toolbarStart,
  hideToolbar = false,
  hideFooter = false,
  selectedLabel = "Selected",
  actionDropdownLabel = "Action",
  selectAllLabel = "Select All",
  getRowClassName,
  tableMinWidth,
  tableFooter,
}) {
  const { t } = useTranslation("translation");
  const isIndexMode = selectionMode === "index";
  const showCheckboxes = selectable && selectionMode === "checkbox";

  const [internalSelected, setInternalSelected] = useState(new Set());
  const selectedSet =
    controlledSelectedIds != null
      ? new Set(controlledSelectedIds.map(String))
      : internalSelected;

  const setSelected = useCallback(
    (next) => {
      const ids = typeof next === "function" ? next(selectedSet) : next;
      const arr = [...ids];
      if (onSelectionChange) onSelectionChange(arr);
      else setInternalSelected(new Set(arr));
    },
    [onSelectionChange, selectedSet]
  );

  const storageKey = tableId ? `oh-table-cols-${tableId}` : null;
  const [visibleIds, setVisibleIds] = useState(() =>
    loadVisibleColumnIds(columns, storageKey)
  );

  const prevColumnIdsRef = useRef(null);

  /* Drop stale ids and add only newly introduced columns. Do not re-show user-hidden columns. */
  useEffect(() => {
    const columnIdSet = new Set(columns.map((c) => c.id));
    setVisibleIds((prev) => {
      const next = new Set([...prev].filter((id) => columnIdSet.has(id)));
      const prevIds = prevColumnIdsRef.current;
      if (prevIds) {
        columns.forEach((c) => {
          if (c.visible !== false && !prevIds.has(c.id)) next.add(c.id);
        });
      } else {
        // First effect after mount: merge any brand-new columns not in hydrated prefs.
        const hydrated = loadVisibleColumnIds(columns, storageKey);
        hydrated.forEach((id) => next.add(id));
      }
      if (next.size === 0) {
        defaultVisibleColumnIds(columns).forEach((id) => next.add(id));
      }
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev;
      }
      return next;
    });
    prevColumnIdsRef.current = columnIdSet;
  }, [columns, tableId, storageKey]);

  useEffect(() => {
    persistVisibleColumnIds(storageKey, visibleIds, columns);
  }, [visibleIds, storageKey, columns]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkRef = useRef(null);
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(() => {
    const fromProp = Number(pagination?.pageSize);
    const fromOptions = Number(pagination?.pageSizeOptions?.[0]);
    return fromProp || fromOptions || DEFAULT_TABLE_PAGE_SIZE;
  });
  const controlledPagination = isControlledPagination(pagination);

  const resolvedPagination = useMemo(() => {
    if (hideFooter) return null;
    if (controlledPagination) {
      return {
        pageSize: DEFAULT_TABLE_PAGE_SIZE,
        total: data.length,
        pageSizeOptions: [DEFAULT_TABLE_PAGE_SIZE, 20, 25, 50],
        ...pagination,
      };
    }
    if (!pagination && data.length === 0) return null;
    return {
      page: clientPage,
      pageSize: clientPageSize,
      total: data.length,
      onPageChange: setClientPage,
      onPageSizeChange: (size) => {
        setClientPageSize(size);
        setClientPage(1);
      },
      pageSizeOptions: pagination?.pageSizeOptions,
      showLabel: pagination?.showLabel,
      ofLabel: pagination?.ofLabel,
      resultsLabel: pagination?.resultsLabel,
      isRtl: pagination?.isRtl ?? isRtl,
      previousLabel: pagination?.previousLabel,
      nextLabel: pagination?.nextLabel,
    };
  }, [
    pagination,
    controlledPagination,
    hideFooter,
    data.length,
    clientPage,
    clientPageSize,
    isRtl,
  ]);

  useEffect(() => {
    if (controlledPagination) return;
    const totalPages = Math.max(1, Math.ceil(data.length / clientPageSize) || 1);
    if (clientPage > totalPages) setClientPage(totalPages);
  }, [data.length, clientPage, clientPageSize, controlledPagination]);

  const clientDataSignature = useMemo(() => {
    if (controlledPagination) return "";
    if (!data.length) return "0";
    const first = getRowId(data[0], rowKey, 0);
    const last = getRowId(data[data.length - 1], rowKey, data.length - 1);
    return `${data.length}:${first}:${last}`;
  }, [controlledPagination, data, rowKey]);

  useEffect(() => {
    if (controlledPagination) return;
    setClientPage(1);
  }, [clientDataSignature, controlledPagination]);

  const tableData = useMemo(() => {
    if (hideFooter || !resolvedPagination) return data;
    if (controlledPagination) return data;
    const start = (resolvedPagination.page - 1) * resolvedPagination.pageSize;
    return data.slice(start, start + resolvedPagination.pageSize);
  }, [data, hideFooter, resolvedPagination, controlledPagination]);

  useEffect(() => {
    const handleClick = (e) => {
      if (bulkRef.current && !bulkRef.current.contains(e.target)) setBulkOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const hideableCols = columns.filter((c) => c.hideable !== false);

  const visibleColumns = useMemo(() => {
    const cols = columns.filter((c) => visibleIds.has(c.id));
    if (cols.length > 0) return cols;
    const fallback =
      hideableCols[0] ||
      columns.find((c) => c.visible !== false) ||
      columns[0];
    return fallback ? [fallback] : [];
  }, [columns, visibleIds, hideableCols]);

  const rowIds = useMemo(
    () => tableData.map((row, i) => getRowId(row, rowKey, i)),
    [tableData, rowKey]
  );

  const allSelected = tableData.length > 0 && rowIds.every((id) => selectedSet.has(id));
  const someSelected = rowIds.some((id) => selectedSet.has(id));
  const hasSelection = selectedSet.size > 0;

  const toggleRow = (id, e) => {
    e?.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rowIds));
  };

  const handleHeaderSort = (col, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!col.sortable || !sort?.onChange) return;
    const next = toggleSort(sort.column, sort.direction, col.id);
    sort.onChange(next);
  };

  const toolbarDisabled =
    disableToolbarWhenSelected && hasSelection && bulkBarPlacement !== "end";

  const showBulkInToolbar = showCheckboxes && hasSelection;
  const bulkInStart = showBulkInToolbar && bulkBarPlacement === "start";
  const bulkInEnd = showBulkInToolbar && bulkBarPlacement === "end";

  const countVisibleHideable = (ids) =>
    hideableCols.filter((c) => ids.has(c.id)).length;

  const toggleColumn = (id) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (
          hideableCols.some((c) => c.id === id) &&
          countVisibleHideable(next) <= 1
        ) {
          return prev;
        }
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAllColumns = (visible) => {
    if (!visible) return;
    setVisibleIds((prev) => {
      const next = new Set(prev);
      hideableCols.forEach((c) => next.add(c.id));
      columns
        .filter((c) => c.hideable === false)
        .forEach((c) => next.add(c.id));
      return next;
    });
  };

  const resolvedTableMinWidth = useMemo(
    () =>
      computeTableMinWidth(visibleColumns, {
        showCheckboxes,
        isIndexMode,
        explicitMinWidth: tableMinWidth,
      }),
    [visibleColumns, showCheckboxes, isIndexMode, tableMinWidth]
  );

  const cardStyle = {
    ...(maxBodyHeight
      ? { "--oh-table-max-height": typeof maxBodyHeight === "number" ? `${maxBodyHeight}px` : maxBodyHeight }
      : {}),
    ...(resolvedTableMinWidth
      ? { "--oh-table-min-width": `${resolvedTableMinWidth}px` }
      : {}),
  };

  const renderBulkBar = (inHead = false) => (
    <div className={`oh-table-bulk-bar${inHead ? " oh-table-bulk-bar--in-head" : ""}`}>
      {!inHead && (
        <div className="oh-table-checkbox-wrap">
          <input
            type="checkbox"
            className="oh-table-checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={toggleAll}
            aria-label={selectAllLabel}
          />
        </div>
      )}
      <span className="oh-table-bulk-bar__count">
        {selectedSet.size} {selectedLabel}
      </span>
      {bulkActions.length > 0 && (
        <div className="oh-table-bulk-dropdown" ref={bulkRef}>
          <button
            type="button"
            className="oh-table-bulk-dropdown__toggle"
            onClick={() => setBulkOpen((o) => !o)}
          >
            {actionDropdownLabel}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {bulkOpen && (
            <div className="oh-table-bulk-dropdown__menu">
              {bulkActions.map((action, i) => (
                <button
                  key={action.label || i}
                  type="button"
                  className="oh-table-bulk-dropdown__item"
                  onClick={() => {
                    setBulkOpen(false);
                    action.onClick?.([...selectedSet]);
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderToolbar = () => {
    if (hideToolbar) return null;

    return (
      <div className={`oh-table-toolbar${toolbarStart ? " oh-table-toolbar--tabs" : ""}`}>
        <div className="oh-table-toolbar__start">
          {bulkInStart ? (
            renderBulkBar(true)
          ) : (
            <>
              {toolbarStart}
              {title ? <h2 className="oh-table-toolbar__title">{title}</h2> : null}
            </>
          )}
        </div>
        <div className="oh-table-toolbar__actions">
          {bulkInEnd ? renderBulkBar(true) : null}
          {toolbarExtra}
          {primaryAction && (
            <Button
              variant="primary"
              size="table"
              className="oh-table-toolbar__primary-btn"
              icon={resolvePrimaryActionIcon(primaryAction)}
              disabled={toolbarDisabled || primaryAction.disabled}
              onClick={primaryAction.onClick}
              title={primaryAction.title}
            >
              {primaryAction.label}
            </Button>
          )}
          {showColumnSettings && hideableCols.length > 0 && (
            <div className="oh-table-settings-wrap">
              <button
                type="button"
                className="oh-table-toolbar__settings"
                disabled={toolbarDisabled}
                aria-label={columnSettingsAriaLabel || "Column settings"}
                onClick={() => setSettingsOpen((o) => !o)}
              >
                {settingsIcon || (
                  <SlidersHorizontal size={20} strokeWidth={2} aria-hidden="true" />
                )}
              </button>
              {settingsOpen && (
                <ColumnVisibilityMenu
                  columns={columns}
                  visibleIds={visibleIds}
                  onToggle={toggleColumn}
                  onToggleAll={toggleAllColumns}
                  onClose={() => setSettingsOpen(false)}
                  selectAllLabel={selectAllLabel}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const startIndex = resolvedPagination
    ? ((resolvedPagination.page || 1) - 1) * (resolvedPagination.pageSize || DEFAULT_TABLE_PAGE_SIZE)
    : 0;

  const showLoadingEmpty = loading && tableData.length === 0;
  const showLoadingBody = loading && tableData.length > 0;

  const thAlignClass = (align) => {
    if (align === "center") return "oh-table-th--center";
    // "end" = logical end (flips under RTL); "right" = physical right
    if (align === "end") return "oh-table-th--end";
    if (align === "right") return "oh-table-th--physical-right";
    return "";
  };

  /** Header alignment matches the cell so labels line up with values. */
  const resolveHeaderAlign = (col) => {
    if (col.headerAlign) return col.headerAlign;
    return col.align || "start";
  };

  const sortBtnAlignClass = (align) => {
    if (align === "center") return "oh-table-sort-btn--center";
    if (align === "end" || align === "right") return "oh-table-sort-btn--end";
    return "";
  };

  const labelWrapAlignClass = (align) => {
    if (align === "center") return "oh-table-th__label-wrap--center";
    if (align === "end" || align === "right") return "oh-table-th__label-wrap--end";
    return "";
  };

  const tdAlignClass = (align) => {
    if (align === "center") return "oh-table-td--center";
    // "end" = logical end (flips under RTL); "right" = physical right
    if (align === "end") return "oh-table-td--end";
    if (align === "right") return "oh-table-td--physical-right";
    return "";
  };

  const colStyle = (col) => resolveColumnWidth(col);

  const colgroup = (
    <colgroup>
      {showCheckboxes && <col style={{ width: 52 }} />}
      {isIndexMode && <col style={{ width: 48 }} />}
      {visibleColumns.map((col) => (
        <col key={col.id} style={colStyle(col)} />
      ))}
    </colgroup>
  );

  const tableHead = (
    <thead className="oh-table-head">
      <tr className="oh-table-head-row">
        {showCheckboxes && (
          <th className="oh-table-checkbox-col" scope="col">
            <div className="oh-table-checkbox-wrap">
              <input
                type="checkbox"
                className="oh-table-checkbox oh-table-checkbox--header"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={toggleAll}
                aria-label={selectAllLabel}
              />
            </div>
          </th>
        )}
        {isIndexMode && (
          <th className="oh-table-index-col" scope="col">
            <span className="oh-table-th__label-wrap oh-table-th__label-wrap--center">#</span>
          </th>
        )}
        {visibleColumns.map((col) => {
          const headerLabel = col.header ?? col.label ?? col.id;
          const isSortable = col.sortable && sort?.onChange;
          const headerAlign = resolveHeaderAlign(col);
          const thClass = [
            thAlignClass(headerAlign),
            isSortable ? "oh-table-th--sortable" : "",
            col.headerClassName || "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <th
              key={col.id}
              scope="col"
              className={thClass}
              style={colStyle(col)}
              aria-sort={
                isSortable && sort?.column === col.id
                  ? sort.direction === "asc"
                    ? "ascending"
                    : "descending"
                  : isSortable
                    ? "none"
                    : undefined
              }
            >
              {isSortable ? (
                <SortableHeaderButton
                  headerLabel={headerLabel}
                  headerAlign={headerAlign}
                  colId={col.id}
                  sort={sort}
                  sortBtnAlignClass={sortBtnAlignClass}
                  onSort={(e) => handleHeaderSort(col, e)}
                />
              ) : (
                <span
                  className={[
                    "oh-table-th__label-wrap",
                    labelWrapAlignClass(headerAlign),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <TableHeaderLabel className="oh-table-th__label">
                    {headerLabel}
                  </TableHeaderLabel>
                </span>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const totalColSpan =
    visibleColumns.length + (showCheckboxes ? 1 : 0) + (isIndexMode ? 1 : 0);

  return (
    <div
      className={`oh-table-card ${className}`.trim()}
      dir={isRtl ? "rtl" : undefined}
      style={Object.keys(cardStyle).length ? cardStyle : undefined}
    >
      {renderToolbar()}

      {hideToolbar && hasSelection && showCheckboxes && (
        <div className="oh-table-bulk-bar">{renderBulkBar()}</div>
      )}

      <div
        className={[
          "oh-table-scroll",
          "oh-table-body-wrap",
          showLoadingBody ? "oh-table-body-wrap--loading" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-busy={showLoadingBody || undefined}
      >
        <table className="oh-table" dir={isRtl ? "rtl" : undefined}>
          {colgroup}
          {tableHead}
          <tbody>
            {showLoadingEmpty ? (
              <tr>
                <td colSpan={totalColSpan}>
                  <div className="oh-table-loading">{t("Loading…")}</div>
                </td>
              </tr>
            ) : tableData.length === 0 ? (
              <tr>
                <td colSpan={totalColSpan}>
                  <div className="oh-table-empty">{emptyState || t("No Data Available")}</div>
                </td>
              </tr>
            ) : (
              tableData.map((row, i) => {
                const id = rowIds[i];
                const selected = selectedSet.has(id);
                const clickable = Boolean(onRowClick);
                return (
                  <tr
                    key={id}
                    className={[
                      getRowClassName?.(row, i) || "",
                      selected ? "oh-table-row--selected" : "",
                      clickable ? "oh-table-row--clickable" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={clickable ? () => onRowClick(row, i) : undefined}
                  >
                    {showCheckboxes && (
                      <td className="oh-table-checkbox-col">
                        <div className="oh-table-checkbox-wrap">
                          <input
                            type="checkbox"
                            className="oh-table-checkbox"
                            checked={selected}
                            onChange={(e) => toggleRow(id, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </td>
                    )}
                    {isIndexMode && (
                      <td className="oh-table-index-col">{startIndex + i + 1}</td>
                    )}
                    {visibleColumns.map((col) => (
                      <td
                        key={col.id}
                        style={colStyle(col)}
                        className={[
                          col.strong ? "oh-table-td--strong" : "",
                          tdAlignClass(col.align),
                          col.nowrap ? "oh-table-td--nowrap" : "oh-table-td--wrap",
                          col.className || "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {col.render ? col.render(row, i) : row[col.id]}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
            {tableFooter && tableData.length > 0 ? (
              <tr className="oh-table-footer-row">
                {showCheckboxes ? (
                  <td className="oh-table-footer-row__cell oh-table-footer-row__cell--empty" />
                ) : null}
                {isIndexMode ? (
                  <td className="oh-table-footer-row__cell oh-table-footer-row__cell--empty" />
                ) : null}
                <td className="oh-table-footer-row__cell">{tableFooter}</td>
                {visibleColumns.slice(1).map((col) => (
                  <td
                    key={col.id}
                    className="oh-table-footer-row__cell oh-table-footer-row__cell--empty"
                  />
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {!hideFooter && resolvedPagination && (
        <TablePagination
          page={resolvedPagination.page}
          pageSize={resolvedPagination.pageSize}
          total={resolvedPagination.total}
          onPageChange={resolvedPagination.onPageChange}
          onPageSizeChange={resolvedPagination.onPageSizeChange}
          pageSizeOptions={resolvedPagination.pageSizeOptions}
          showLabel={resolvedPagination.showLabel}
          ofLabel={resolvedPagination.ofLabel}
          resultsLabel={resolvedPagination.resultsLabel}
          isRtl={isRtl || resolvedPagination.isRtl}
          previousLabel={resolvedPagination.previousLabel}
          nextLabel={resolvedPagination.nextLabel}
        />
      )}
    </div>
  );
}

export default DataTable;
